// Domain · CategoryChip
//
// Factory-bound chip for the `CompanyCategory` enum.
// Reuses `CATEGORY_COLORS` from `@ds/tokens/domainTones`.
// Polish labels from `companies.categories.*` i18next keys.

import React from "react";
import { CATEGORY_COLORS } from "@ds/tokens/domainTones";
import type { CompanyCategory } from "@shared/types";
import { createPillChip } from "@ds/domain/EnumTag.factories";
import { Inline } from "@ds/layout/Inline";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface CategoryChipInput {
  /** Category enum value. `null` / `undefined` → em-dash. */
  category: CompanyCategory | null | undefined;
  /** Rendering variant. Default `"chip"`. */
  variant?: "chip" | "inline";
  /** Pill size — only meaningful when `variant="chip"`. Default `"md"`. */
  size?: "sm" | "md";
  /** `data-testid` on the root. Defaults to `"category-chip"`. */
  testId?: string;
}

export type CategoryChipProps = CategoryChipInput;

// ─── Component ────────────────────────────────────────────────────────────

const _Base = createPillChip<CompanyCategory>(
  CATEGORY_COLORS,
  "companies.categories",
  { valueAsFallback: true, defaultTestId: "category-chip" },
);

export function CategoryChip({
  category,
  variant = "chip",
  size = "md",
  testId = "category-chip",
}: CategoryChipProps) {
  return <_Base value={category} variant={variant} size={size} testId={testId} />;
}

// ─── CategoryRow — multi-category chip strip ──────────────────────────────

export interface CategoryRowInput {
  /** Category enum values. `null` / empty → em-dash. */
  categories: CompanyCategory[] | null | undefined;
  /** Pill size — forwarded to each `CategoryChip`. Default `"sm"`. */
  size?: "sm" | "md";
  /** `data-testid` on the root. Defaults to `"category-row"`. */
  testId?: string;
}

export type CategoryRowProps = CategoryRowInput;

export function CategoryRow({
  categories,
  size = "sm",
  testId = "category-row",
}: CategoryRowProps) {
  if (!categories || categories.length === 0) {
    return <span data-testid={testId}>—</span>;
  }
  return (
    <Inline as="span" inline wrap gap="xs" testId={testId}>
      {categories.map((c) => (
        <CategoryChip key={c} category={c} size={size} />
      ))}
    </Inline>
  );
}
